/**
 * @typedef {{ id: number, title: string, url: string|null, note: string|null }} Reference
 */

/**
 * @typedef {object} EnergySource
 * @property {number} id
 * @property {string} name
 * @property {string|null} source
 * @property {number} priority_order higher is the later source
 * @property {number} factor_count
 * @property {Reference|null} reference
 */

/**
 * @typedef {object} EnergyFactor
 * @property {number} id
 * @property {number|null} kpi_id
 * @property {string|null} kpi_title
 * @property {number|null} source_id
 * @property {string|null} source_name
 * @property {string} country_code
 * @property {number|null} unit_id
 * @property {string} unit
 * @property {string|null} fuel_type
 * @property {number|null} fuel_density
 * @property {number|null} calorific_value
 * @property {Reference|null} density_reference
 * @property {Reference|null} calorific_reference
 */

/**
 * @typedef {{ kpi_id: number, title: string, units: { id: number, unit: string }[] }} EnergyKpi
 */

/** The values of an energy factor that each cite their own reference. */
export const ENERGY_REFERENCE_TARGETS = Object.freeze([
  { target: "density", label: "Fuel density" },
  { target: "calorific", label: "Calorific value" },
]);

export const FACTOR_LINK_FILTERS = Object.freeze([
  { value: "", label: "Any link" },
  { value: "missing", label: "Missing a link" },
  { value: "complete", label: "Both values linked" },
]);

export const EMPTY_FACTOR_FILTERS = Object.freeze({ search: "", sourceId: "", link: "" });

/**
 * @param {EnergyFactor} factor
 * @returns {number|null} GJ per unit of the reading, as pricing works it out
 */
export const energyPerUnit = (factor) =>
  factor.fuel_density == null || factor.calorific_value == null ? null : factor.fuel_density * factor.calorific_value;

/**
 * @param {number|null} value
 * @returns {string} up to 6 significant digits, or "—"
 */
export const formatValue = (value) => (value == null ? "—" : String(Number(value.toPrecision(6))));

/**
 * The factors the filters keep: the search matches the KPI title or fuel
 * type, and the link filter asks whether both values cite a reference.
 *
 * @param {EnergyFactor[]} factors
 * @param {{ search: string, sourceId: string, link: string }} filters
 * @returns {EnergyFactor[]}
 */
export const filterFactors = (factors, { search, sourceId, link }) => {
  const needle = search.trim().toLowerCase();
  return factors.filter((factor) => {
    if (needle && !`${factor.kpi_title || ""} ${factor.fuel_type || ""}`.toLowerCase().includes(needle)) return false;
    if (sourceId && String(factor.source_id) !== sourceId) return false;
    const complete = Boolean(factor.density_reference && factor.calorific_reference);
    if (link === "missing") return !complete;
    if (link === "complete") return complete;
    return true;
  });
};

/**
 * @typedef {object} SourceDraft
 * @property {number|null} id null for a new source
 * @property {string} name
 * @property {string} source
 * @property {string} priority
 * @property {string} referenceId "" for none
 */

/** @returns {SourceDraft} */
export const emptySourceDraft = () => ({ id: null, name: "", source: "", priority: "", referenceId: "" });

/**
 * @param {EnergySource} source
 * @returns {SourceDraft}
 */
export const draftFromSource = (source) => ({
  id: source.id,
  name: source.name,
  source: source.source || "",
  priority: String(source.priority_order),
  referenceId: source.reference ? String(source.reference.id) : "",
});

/**
 * @param {SourceDraft} draft
 * @param {EnergySource[]} sources every source, for the duplicate name check
 * @returns {string} what is wrong, or "" when it can be saved
 */
export const sourceDraftError = (draft, sources) => {
  const name = draft.name.trim();
  if (!name) return "Name is required";
  if (sources.some((source) => source.id !== draft.id && source.name.trim().toLowerCase() === name.toLowerCase())) {
    return `An energy source named "${name}" already exists`;
  }
  const priority = draft.priority.trim();
  if (priority && !(/^\d+$/.test(priority) && Number(priority) >= 1)) return "Priority must be a whole number from 1";
  return "";
};

/**
 * @param {SourceDraft} draft
 * @returns {{ name: string, source: string, priority_order: number|null, reference_id: number|null }}
 */
export const sourceDraftToPayload = (draft) => ({
  name: draft.name.trim(),
  source: draft.source.trim(),
  priority_order: draft.priority.trim() ? Number(draft.priority) : null,
  reference_id: draft.referenceId ? Number(draft.referenceId) : null,
});

/**
 * @typedef {object} FactorDraft
 * @property {number|null} id null for a new factor
 * @property {string} kpiId
 * @property {string} unitId
 * @property {string} sourceId
 * @property {string} countryCode
 * @property {string} fuelType
 * @property {string} density
 * @property {string} calorific
 * @property {string} densityReferenceId "" for none
 * @property {string} calorificReferenceId "" for none
 */

/**
 * @param {string} sourceId the source a new factor starts in
 * @returns {FactorDraft}
 */
export const emptyFactorDraft = (sourceId = "") => ({
  id: null,
  kpiId: "",
  unitId: "",
  sourceId,
  countryCode: "IN",
  fuelType: "",
  density: "",
  calorific: "",
  densityReferenceId: "",
  calorificReferenceId: "",
});

/** @param {number|null} value @returns {string} */
const text = (value) => (value == null ? "" : String(value));

/**
 * @param {EnergyFactor} factor
 * @returns {FactorDraft}
 */
export const draftFromFactor = (factor) => ({
  id: factor.id,
  kpiId: text(factor.kpi_id),
  unitId: text(factor.unit_id),
  sourceId: text(factor.source_id),
  countryCode: factor.country_code,
  fuelType: factor.fuel_type || "",
  density: text(factor.fuel_density),
  calorific: text(factor.calorific_value),
  densityReferenceId: factor.density_reference ? String(factor.density_reference.id) : "",
  calorificReferenceId: factor.calorific_reference ? String(factor.calorific_reference.id) : "",
});

/** @param {string} value @returns {boolean} */
const isPositive = (value) => value.trim() !== "" && Number.isFinite(Number(value)) && Number(value) > 0;

/**
 * The rules the backend applies, plus a second factor for one KPI, source
 * and country, so a bad draft is caught in the form.
 *
 * @param {FactorDraft} draft
 * @param {EnergyFactor[]} factors every factor, for the duplicate check
 * @param {EnergyKpi[]} kpis
 * @returns {string} what is wrong, or "" when it can be saved
 */
export const factorDraftError = (draft, factors, kpis) => {
  const kpi = kpis.find((candidate) => String(candidate.kpi_id) === draft.kpiId);
  if (!kpi) return "Pick a KPI";
  if (!kpi.units.some((unit) => String(unit.id) === draft.unitId)) return "Pick a unit the KPI is reported in";
  if (!draft.sourceId) return "Pick an energy source";
  const country = draft.countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2,3}$/.test(country)) return "Country must be a 2 or 3 letter code such as IN";
  if (!isPositive(draft.density)) return "Fuel density must be a number above zero";
  if (!isPositive(draft.calorific)) return "Calorific value must be a number above zero";
  const taken = factors.some(
    (factor) =>
      factor.id !== draft.id &&
      String(factor.kpi_id) === draft.kpiId &&
      String(factor.source_id) === draft.sourceId &&
      factor.country_code === country
  );
  if (taken) return `${kpi.title} already has a factor in this source for ${country}`;
  return "";
};

/**
 * @param {FactorDraft} draft
 * @returns {{ kpi_id: number, unit_id: number, source_id: number, country_code: string, fuel_type: string, fuel_density: number, calorific_value: number, density_reference_id: number|null, calorific_reference_id: number|null }}
 */
export const factorDraftToPayload = (draft) => ({
  kpi_id: Number(draft.kpiId),
  unit_id: Number(draft.unitId),
  source_id: Number(draft.sourceId),
  country_code: draft.countryCode.trim().toUpperCase(),
  fuel_type: draft.fuelType.trim(),
  fuel_density: Number(draft.density),
  calorific_value: Number(draft.calorific),
  density_reference_id: draft.densityReferenceId ? Number(draft.densityReferenceId) : null,
  calorific_reference_id: draft.calorificReferenceId ? Number(draft.calorificReferenceId) : null,
});
